import type { LocationQuery } from 'vue-router'

import EventBus from '@/modules/event-bus'
import { createSafeStorage } from '@/modules/safe-storage'
import { SSR_QUERY } from '@/router/query-params'
import type { GameEvent } from '@/types'
import { loadScript } from '@/utils/loadScript'

import { MessageAction } from '../message-types'

export const MINIPLAY_CLID = 'p_a68073c6-abde-4a55-a494-5ddb9ec8baa0'

declare class _LeChuckAPI {
    constructor(_param: unknown)
    stat: {
        put(callback: (res: unknown) => void, stat: string, value: number): void
    }
}

declare global {
    interface Window {
        LeChuckAPI?: typeof _LeChuckAPI
    }
}

type GameStorageHandler = (storage: Record<string, string>) => Promise<void> | void

async function decodeBase64Gzip(base64Str: string) {
    // 1. Decode base64 → Uint8Array
    const binaryStr = atob(base64Str)
    const len = binaryStr.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
        bytes[i] = binaryStr.charCodeAt(i)
    }

    // 2. Decompress with DecompressionStream (gzip)
    const ds = new DecompressionStream('gzip')
    const decompressedStream = new Response(new Blob([bytes]).stream().pipeThrough(ds))

    // // 3. Get the text directly
    return await decompressedStream.text()
}

export class MiniplayIntegration {
    private sdkPromise: Promise<_LeChuckAPI | undefined>
    private reportedStat = new Set<string>()
    private enabled
    private gameHRU
    private storageMessageHandler
    constructor({
        queryParams,
        gameHRU,
        storageMessageHandler,
    }: {
        queryParams: LocationQuery
        gameHRU: string
        storageMessageHandler: ReturnType<typeof createSafeStorage>
    }) {
        this.enabled = queryParams[SSR_QUERY.CLID] === MINIPLAY_CLID
        this.gameHRU = gameHRU
        this.storageMessageHandler = storageMessageHandler
        this.sdkPromise = Promise.resolve(undefined)
        if (this.enabled) {
            this.sdkPromise = loadScript({
                src: 'https://ssl.minijuegosgratis.com/lechuck/js/latest.js',
                scriptName: 'Miniplay LeChuck',
                useExisting: true,
            }).then((result) => {
                if (result.status !== 'ok') {
                    return undefined
                }
                if (!window.LeChuckAPI) {
                    console.error("Cannot instantiate Minijuego's LeChuckAPI")
                    return undefined
                }
                return new window.LeChuckAPI({})
            })
        }
    }

    async listen(bus: EventBus<Record<string, Record<string, unknown>>>) {
        if (this.enabled) {
            bus.addEventListener('storage', this.handleMessage)
            bus.addEventListener('cloud-save', this.handleMessage)

            // TODO support cloud-saves
            const getStateResponseEvent = await this.storageMessageHandler({
                data: {
                    action: MessageAction.getState,
                },
            } as GameEvent)
            const storedPayload = getStateResponseEvent?.payload as Record<string, unknown>

            if (storedPayload?.state) {
                this.handleMessage(
                    new CustomEvent('storage', {
                        detail: {
                            type: 'storage',
                            payload: storedPayload.state,
                        },
                    }),
                )
            }
        }
    }

    async reportStat(stat: string, value: number) {
        const lechunkAPI = await this.sdkPromise
        const reportKey = `${stat}-${value}`
        if (this.reportedStat.has(reportKey)) {
            return
        }
        this.reportedStat.add(reportKey)
        console.info(`[PG<->MINIPLAY] Reporting stat ${stat}: ${value}`)
        lechunkAPI?.stat.put(
            (response) => {
                console.info('window.lechuck.stat.put response', response)
            },
            stat,
            value,
        )
    }

    handleMessage = async (event: CustomEvent<Record<string, unknown>>) => {
        const data = event.detail
        if ('type' in data && data.type === 'storage') {
            const payload = data.payload as Record<string, string>
            const handlers: Record<string, GameStorageHandler> = {
                'mr-racer-car-racing': this.handleMRRacer,
                'geometry-arrow': this.handleGeometryArrow,
                'geometry-arrow-2': this.handleGeometryArrow2,
                'gangster-island-crime-city': this.handleGangstaIsland,
                'rooftop-run': this.handleRooftopRun,
                'zombie-space-episode-ii': this.handleZombieSpaceEpisodeII,
                'crazy-bus-station': this.handleCrazyBusStation,
                'archery-ragdoll': this.handleArcheryRagdoll,
            }
            if (this.gameHRU in handlers) {
                try {
                    await handlers[this.gameHRU]?.(payload)
                } catch (e) {
                    console.error('Error during message handle', data, e)
                }
            }
        }
    }
    handleMRRacer: GameStorageHandler = (storage) => {
        const state = JSON.parse(storage.mrr || '') as {
            carsUnlocked: string
            locationLockStatus: string
            achievementsUnlocked: string
            maxSpeed: string
        }
        state.carsUnlocked.split('|').forEach((carState, index) => {
            if (carState === '0') {
                // 0 based
                this.reportStat(`carsUnlocked${index + 1}`, 1)
            }
        })
        state.locationLockStatus.split('|').forEach((locationState, index) => {
            if (locationState === '0') {
                // 0 based
                this.reportStat(`locationUnlocked${index + 1}`, 1)
            }
        })
        state.achievementsUnlocked.split('|').forEach((achievementState, index) => {
            if (achievementState === '1') {
                // 0 based
                this.reportStat(`achievementUnlocked${index + 1}`, 1)
            }
        })

        this.reportStat(`maxSpeed`, Number(state.maxSpeed))
    }

    handleGeometryArrow: GameStorageHandler = (storage) => {
        Object.entries(storage).forEach(([key, value]) => {
            const levelMatch = key.match(/level(\d+)_progress/)
            if (levelMatch && value === '1') {
                // levels are 1 based
                this.reportStat(`lvl${levelMatch[1]}`, 1)
            }
        })
    }
    handleGeometryArrow2: GameStorageHandler = (storage) => {
        Object.entries(storage).forEach(([key, value]) => {
            const levelMatch = key.match(/level(\d+)_progress/)
            if (levelMatch && value === '1') {
                // levels are 0 based
                const level = Number(levelMatch[1]) + 1
                this.reportStat(`lvl${level}`, 1)
            }
        })
        if (storage.global_score) {
            this.reportStat(`globalScore`, Number(storage.global_score))
        }
        if (storage.total_deaths) {
            this.reportStat(`totalDeaths`, Number(storage.total_deaths))
        }
    }
    handleGangstaIsland: GameStorageHandler = async (storage) => {
        if ('product_chunks_pg' in storage) {
            let base64str = ''
            for (let i = 0; i < Number(storage.product_chunks_pg); i++) {
                base64str += storage[`product_chunk_pg_${i}`]
            }
            const state = JSON.parse(await decodeBase64Gzip(base64str)) as {
                tileSaves: { values: { unlocked: boolean }[] }[]
            }
            state.tileSaves.forEach((innerTiles, index) => {
                const levelProgress = innerTiles.values.filter((x) => x.unlocked).length
                if (levelProgress > 0) {
                    this.reportStat(`level_${index + 1}_progress`, levelProgress)
                }
            })
        }
    }
    handleRooftopRun: GameStorageHandler = (storage) => {
        if (storage.level && Number(storage.level) > 0) {
            this.reportStat(`level`, Number(storage.level))
        }
        if (storage.coins) {
            this.reportStat(`coins`, Number(storage.coins))
        }
    }
    handleZombieSpaceEpisodeII: GameStorageHandler = (storage) => {
        if (!storage.savesData) {
            return
        }
        const preparedSavesDataJson = storage.savesData.replaceAll('{"Key":"","Value":0,"StrValue":""},', '')
        const savesData = JSON.parse(preparedSavesDataJson) as {
            keys: { Key: string; Value: number }[]
        }
        const money = savesData.keys.find((key) => key.Key === 'MONEY')?.Value
        if (money) {
            this.reportStat(`coins`, Number(money))
        }
        const progressLevel = savesData.keys.find((key) => key.Key === 'progress_level')?.Value
        if (progressLevel && progressLevel > 1) {
            this.reportStat(`level`, progressLevel - 1)
        }
    }
    handleCrazyBusStation: GameStorageHandler = (storage) => {
        if (storage.VIEW_LEVEL && Number(storage.VIEW_LEVEL) > 1) {
            this.reportStat(`level`, Number(storage.VIEW_LEVEL) - 1)
        }
    }
    handleArcheryRagdoll: GameStorageHandler = (storage) => {
        if (!storage.savedArcher) {
            return
        }
        const savesData = JSON.parse(storage.savedArcher) as Record<string, number>
        if (savesData?.level_player && savesData.level_player > 1) {
            this.reportStat('level', savesData.level_player - 1)
        }
        if (savesData?.wave && savesData.wave > 1) {
            this.reportStat('waves', savesData.wave - 1)
        }
    }
}
